/* global localStorage console window */
import { createSlice, current } from "@reduxjs/toolkit";
import { safeStoreTagOnDocument } from "../ppt/safePPT";
import { deepClone } from "../helpers/clone.js";
import redStyle from "../assets/data/styles/red.json";
import greenStyle from "../assets/data/styles/green.json";
import blueStyle from "../assets/data/styles/blue.json";
import blackStyle from "../assets/data/styles/black.json";

const fontFamilies = [
  "Arial",
  "Bahnschrift",
  "Baskerville Old Face",
  "Bell MT",
  "Bodoni MT",
  "Book Antiqua",
  "Bookman Old Style",
  "Calibri",
  "Calibri Light",
  "Calisto MT",
  "Cambria",
  "Candara",
  "Centaur",
  "Century",
  "Century Gothic",
  "Consolas",
  "Constantia",
  "Corbel",
  "Franklin Gothic Book",
  "Garamond",
  "Georgia",
  "Gill Sans MT",
  "Lucida Sans",
  "Palatino Linotype",
  "Perpetua",
  "Rockwell",
  "Sans Serif Collection",
  "Segoe UI",
  "Sylfaen",
  "Tahoma",
  "Times New Roman",
  "Trebuchet MS",
  "Verdana",
];

const factoryDefault = {
  ...blueStyle,
  fontFamilies,
};

export const styleSheetSlice = createSlice({
  name: "styleSheet",
  initialState: {
    selectedSheet: "default",
    factoryDefaults: {
      relativeSizes: {
        Header: 4,
        SubHeader: 2,
        Highlight: 8,
        SmallBody: -2,
        Caption: -4,
      },
    },
    factoryDefaultSheet: { ...factoryDefault },

    sheets: {
      default: { ...factoryDefault },
      Red: { ...redStyle },
      Green: { ...greenStyle },
      Black: { ...blackStyle },
    },

    draftSheets: {},
  },
  reducers: {
    setSelectedSheet: (state, action) => {
      state.selectedSheet = action.payload.sheetName;
    },
    updateSheet: (state, action) => {
      console.log("updateSheet", action);
      if (action.payload.sheetKey === undefined) return;

      let sheet = state.draftSheets[action.payload.sheetKey];
      if (sheet === undefined) {
        if (state.sheets[action.payload.sheetKey] === undefined) {
          sheet = action.payload.from ? { ...state.sheets[action.payload.from] } : { ...state.sheets.default };
          state.draftSheets[action.payload.sheetKey] = sheet;
        } else {
          sheet = deepClone(state.sheets[action.payload.sheetKey]);
          state.draftSheets[action.payload.sheetKey] = sheet;
        }
      }

      if (action.payload.color) {
        const index = sheet.colors.findIndex((c) => c.name === action.payload.color.name);
        if (index !== -1) {
          sheet.colors[index].value = action.payload.color.value;
        }
      }

      if (action.payload.preset) {
        const index = sheet.presets.findIndex((p) => p.name === action.payload.preset.name);
        if (index !== -1) {
          const preset = sheet.presets[index];
          preset.fontFamily = action.payload.preset.fontFamily ?? preset.fontFamily;
          preset.fontWeight = action.payload.preset.fontWeight ?? preset.fontWeight;
          preset.fontStyle = action.payload.preset.fontStyle ?? preset.fontStyle;
          preset.fontSize = action.payload.preset.fontSize ?? preset.fontSize;
          preset.fontColor = action.payload.preset.fontColor ?? preset.fontColor;
          preset.fontColor2 = action.payload.preset.fontColor2 ?? preset.fontColor2;
          preset.fontUnderlined = action.payload.preset.fontUnderlined ?? preset.fontUnderlined;
        }
      }

      if (action.payload.border) {
        const index = sheet.borders.findIndex((b) => b.name === action.payload.border.name);
        if (index !== -1) {
          const border = sheet.borders[index];
          border.color = action.payload.border.color ?? border.color;
          border.width = action.payload.border.width ?? border.width;
          border.type = action.payload.border.type ?? border.type;
        }
      }

      if (action.payload.cellPadding) {
        sheet.cellPadding = action.payload.cellPadding;
      }

      if (action.payload.spacer) {
        const index = sheet.spacerWidths.findIndex((sw) => sw.name === action.payload.spacer.name);
        if (index !== -1) {
          const spacer = sheet.spacerWidths[index];
          spacer.width = action.payload.spacer.width;
        }
      }

      if (action.payload.viz) {
        const { type, index, value } = action.payload.viz;
        if (
          sheet.dataVisualizationColors &&
          sheet.dataVisualizationColors[type] &&
          sheet.dataVisualizationColors[type].length > index
        ) {
          sheet.dataVisualizationColors[type][index] = value;
        }
      }

      if (action.payload.statusColor) {
        const { name, value } = action.payload.statusColor;
        const index = sheet.statusColors.findIndex((c) => c.name === name);
        if (index !== -1) {
          sheet.statusColors[index].value = value;
        }
      }

      if (action.payload.lineSpacing) {
        const { id, paragraph, line } = action.payload.lineSpacing;
        const index = sheet.lineSpacing.findIndex((l) => l.id === id);
        if (index !== -1) {
          sheet.lineSpacing[index].paragraph = paragraph ?? sheet.lineSpacing[index].paragraph;
          sheet.lineSpacing[index].line = line ?? sheet.lineSpacing[index].line;
        }
      }

      if (action.payload.shapePreset) {
        const index = sheet.shapePresets?.findIndex((sp) => sp.name === action.payload.shapePreset.name);
        if (index !== -1) {
          const sp = sheet.shapePresets[index];
          const p = action.payload.shapePreset;
          if (p.shapeType !== undefined) sp.shapeType = p.shapeType;
          if (p.radius !== undefined) sp.radius = p.radius;
          if (p.shapeColor !== undefined) sp.shapeColor = p.shapeColor;
          if (p.outline !== undefined) sp.outline = p.outline;
          if (p.outlineWeight !== undefined) sp.outlineWeight = p.outlineWeight;
          if (p.outlineColor !== undefined) sp.outlineColor = p.outlineColor;
          if (p.shadow !== undefined) sp.shadow = p.shadow;
          if (p.margin !== undefined) sp.margin = p.margin;
          if (p.marginLeft !== undefined) sp.marginLeft = p.marginLeft;
          if (p.marginTop !== undefined) sp.marginTop = p.marginTop;
          if (p.marginRight !== undefined) sp.marginRight = p.marginRight;
          if (p.marginBottom !== undefined) sp.marginBottom = p.marginBottom;
          if (p.textPreset !== undefined) sp.textPreset = p.textPreset;
          if (p.inverted !== undefined) sp.inverted = p.inverted;
          if (p.textAlign !== undefined) sp.textAlign = p.textAlign;
          if (p.verticalAlign !== undefined) sp.verticalAlign = p.verticalAlign;
          if (p.padding !== undefined) sp.padding = p.padding;
          if (p.paddingLeft !== undefined) sp.paddingLeft = p.paddingLeft;
          if (p.paddingTop !== undefined) sp.paddingTop = p.paddingTop;
          if (p.paddingRight !== undefined) sp.paddingRight = p.paddingRight;
          if (p.paddingBottom !== undefined) sp.paddingBottom = p.paddingBottom;
          if (p.marginPerc !== undefined) sp.marginPerc = p.marginPerc;
          if (p.marginLeftPerc !== undefined) sp.marginLeftPerc = p.marginLeftPerc;
          if (p.marginTopPerc !== undefined) sp.marginTopPerc = p.marginTopPerc;
          if (p.marginRightPerc !== undefined) sp.marginRightPerc = p.marginRightPerc;
          if (p.marginBottomPerc !== undefined) sp.marginBottomPerc = p.marginBottomPerc;
          if (p.paddingPerc !== undefined) sp.paddingPerc = p.paddingPerc;
          if (p.paddingLeftPerc !== undefined) sp.paddingLeftPerc = p.paddingLeftPerc;
          if (p.paddingTopPerc !== undefined) sp.paddingTopPerc = p.paddingTopPerc;
          if (p.paddingRightPerc !== undefined) sp.paddingRightPerc = p.paddingRightPerc;
          if (p.paddingBottomPerc !== undefined) sp.paddingBottomPerc = p.paddingBottomPerc;
        }
      }
    },
    applyChangesOnSheet: (state, action) => {
      console.log("applyChangesOnSheet", action);
      if (action.payload.sheetKey === undefined) return;
      if (state.draftSheets[action.payload.sheetKey] === undefined) return;

      state.sheets[action.payload.sheetKey] = { ...state.draftSheets[action.payload.sheetKey] };

      state.draftSheets[action.payload.sheetKey] = undefined;
    },
    applyChangesOnAll: (state) => {
      Object.keys(state.draftSheets).forEach((key) => {
        if (state.draftSheets[key] !== undefined) {
          state.sheets[key] = { ...state.draftSheets[key] };
          state.draftSheets[key] = undefined;
        }
      });
    },
    abandonChangesOnSheet: (state, action) => {
      if (action.payload.sheetKey === undefined) return;
      state.draftSheets[action.payload.sheetKey] = undefined;
    },
    abandonChangesOnAll: (state) => {
      Object.keys(state.draftSheets).forEach((key) => (state.draftSheets[key] = undefined));
    },
    storeSheets: (state, action) => {
      const c = current(state);
      const serialized = JSON.stringify({ selectedSheet: c.selectedSheet, sheets: c.sheets });
      if (action.payload.to === "localStorage") {
        localStorage.setItem("STYLESHEETS", serialized);
      } else if (action.payload.to === "presentation") {
        safeStoreTagOnDocument("GRIDD_STYLESHEET", serialized).catch((r) =>
          console.log("storing stylesheet on presentation failed", r)
        );
      } else if (action.payload.to === "host") {
        // SlideCraft owns persistence: hand the saved sheet to the parent frame.
        // Mirrors the fileSystem.js gridd-save-to-handle bridge. (This reducer
        // already performs I/O side effects, so posting here is consistent.)
        if (typeof window !== "undefined" && window.parent !== window) {
          window.parent.postMessage(
            { type: "gridd-stylesheet-save", payload: { selectedSheet: c.selectedSheet, sheets: c.sheets } },
            "*"
          );
        }
      }
    },
    loadSheets: (state, action) => {
      if (action.payload.sheets) {
        state.sheets = {
          ...state.sheets,
          ...action.payload.sheets,
        };
        state.selectedSheet = action.payload.selectedSheet ?? state.selectedSheet;
      }
    },
    resetFromDefault: (state, action) => {
      if (action.payload.sheetKey) {
        state.draftSheets[action.payload.sheetKey] = { ...state.factoryDefaultSheet };
      }
    },
    rename: (state, action) => {
      if (action.payload.sheetKey && action.payload.to) {
        state.draftSheets[action.payload.to] = state.draftSheets[action.payload.sheetKey];
        state.sheets[action.payload.to] = state.sheets[action.payload.sheetKey];
        state.draftSheets[action.payload.sheetKey] = undefined;
        state.sheets[action.payload.sheetKey] = undefined;
        if (state.selectedSheet === action.payload.sheetKey) {
          state.selectedSheet = action.payload.to;
        }
      }
    },
    importSheet: (state, action) => {
      if (action.payload.sheetKey === undefined) return;
      state.draftSheets[action.payload.sheetKey] = { ...action.payload.sheet };
    },
    deleteSheet: (state, action) => {
      if (action.payload.sheetKey === undefined) return;
      if (action.payload.sheetKey === "default") return;
      state.sheets[action.payload.sheetKey] = undefined;
      state.draftSheets[action.payload.sheetKey] = undefined;
    },
  },
});

export const {
  setSelectedSheet,
  updateSheet,
  applyChangesOnSheet,
  applyChangesOnAll,
  abandonChangesOnSheet,
  abandonChangesOnAll,
  storeSheets,
  loadSheets,
  loadWelcomeSheet,
  resetFromDefault,
  rename,
  importSheet,
  deleteSheet,
} = styleSheetSlice.actions;

export default styleSheetSlice.reducer;
